import { useCallback, useRef } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import { trackSurfaceActivityDropped } from 'tripPlanner/utils/chatAnalytics'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'

// Different concern from buildTripPlannerSnapshot.ts's SURFACE_ACTIVITY_PUBLISH_MAX: the
// accumulator only drains at turn submit, so a customer browsing without sending for twenty
// minutes would otherwise grow it unboundedly. Bounded at the same number as the publish cap so
// the publish-time slice is a no-op in the ordinary case, independent of whether a turn ever
// happens.
const SURFACE_ACTIVITY_ACCUMULATOR_MAX = 40

// Shared by both the push handler and restoreDrainedActivity below -- both can grow the buffer
// past the cap and both must report the drop the same way. A drop here is customer activity lost
// before it ever reaches the wire, on either path, so neither call site gets to skip the report.
function capAndTrackDrops(entries: Array<TripPlannerSurfaceActivityEntry>): Array<TripPlannerSurfaceActivityEntry> {
  const overflow = entries.length - SURFACE_ACTIVITY_ACCUMULATOR_MAX
  if (overflow <= 0) return entries
  // Matches trackAssistantSurfaceSnapshotOutcome's existing pattern (chatAnalytics.ts) for
  // reporting a degraded-but-handled outcome as a named, countable signal, rather than letting
  // this cap evict silently.
  trackSurfaceActivityDropped(overflow)
  return entries.slice(-SURFACE_ACTIVITY_ACCUMULATOR_MAX)
}

// A genuine sibling, not a generalisation, of a future committed-state accumulator: container is
// a plain array, not a Map; read semantics are drain, not prune-and-retain; there is no
// TTL/isLive check; wire order is reversed push order, not a sort.
export function useSurfaceActivityAccumulator() {
  const liveRef = useRef<Array<TripPlannerSurfaceActivityEntry>>([])

  useEventSubscription('trip_planner_surface_activity', event => {
    liveRef.current.push(...event.entries)
    liveRef.current = capAndTrackDrops(liveRef.current)
  })

  const clearSurfaceActivity = useCallback(() => {
    liveRef.current = []
  }, [])

  // Chat switch clears itself, internally, via this subscription. Logout/user-switch/
  // auth-downgrade do NOT dispatch reset_chat_state (TripPlannerChatProvider.tsx's authScope
  // effect is the separate path for that transition), so clearSurfaceActivity is also exposed
  // below for the provider to call explicitly from there.
  useEventSubscription('reset_chat_state', clearSurfaceActivity)

  // Returns the drained entries directly -- the caller must hold this array itself and pass it
  // to restoreDrainedActivity below on anything other than success (on success, there is
  // nothing to do: the entries are already gone from the live buffer the moment drain returned
  // them). There is deliberately no internal "pending" field here: two resolveTurnAuthority calls
  // can genuinely overlap (overlapping submit/regenerate before the XState machine's
  // disabled-button state commits), and a shared internal ref would let a second call's drain
  // silently clobber the first call's restore target, permanently losing that turn's activity.
  // Explicit parameters make that class of bug structurally impossible -- there is nothing
  // shared left to race over.
  const drainSurfaceActivity = useCallback((): Array<TripPlannerSurfaceActivityEntry> => {
    const drained = liveRef.current
    liveRef.current = []
    return drained
  }, [])

  const restoreDrainedActivity = useCallback((drained: Array<TripPlannerSurfaceActivityEntry>) => {
    if (drained.length === 0) return
    // Sorted by occurredAt, not simply prepended: two overlapping restores can otherwise
    // interleave out of chronological order (call A drains [X], a new entry Y is pushed, call B
    // drains [Y]; if A's restore runs before B's, prepending B's [Y] on top of the already-
    // restored [X] produces [Y, X] -- newer before older). Sorted on the parsed instant, not the
    // raw string, matching svc-trip's own compactSurfaceActivity guard (canonicalise.ts): ISO-8601
    // offsets make lexical order wrong -- "...T23:00:00+10:00" reads later than "...T14:00:00Z"
    // despite happening earlier. Every current emitter uses toISOString() (always UTC), so this
    // can't bite yet, but nothing enforces that for emitters still to come, and this is the one
    // shared place all of them pass through. Capped (and any drop tracked) via the same
    // capAndTrackDrops the push handler uses -- a restore can grow the buffer past the cap just as
    // easily as a push can (this call's own drained entries plus whatever accumulated live during
    // the failed round-trip), and that loss is exactly as real as a push-side drop.
    liveRef.current = capAndTrackDrops(
      sortBy([...drained, ...liveRef.current], entry => Date.parse(entry.occurredAt), 'asc'),
    )
  }, [])

  return { drainSurfaceActivity, restoreDrainedActivity, clearSurfaceActivity }
}
